<script lang="ts" setup>
import AuthLayout from '@/layouts/AuthLayout.vue'
import ProductionBySupplierReportDialog from '@/components/features/reports/ProductionBySupplierReportDialog.vue'
import { getProductionBySupplier } from '@/api/reports'
import { getSupplierList } from '@/api/suppliers'
import { computed, onMounted, ref, watch } from 'vue'
import ProgressSpinner from 'primevue/progressspinner'
import Message from 'primevue/message'
import Dropdown from 'primevue/dropdown'
import Calendar from 'primevue/calendar'
import Button from 'primevue/button'
import { useToast } from 'primevue/usetoast'

import type { ProductionBySupplierReportParams, ProductionBySupplierReportResponse } from '@/types/report-types'
import type { Supplier } from '@/types/supplier-types'

const toast = useToast()

const selectedSupplier = ref<string | null>(null)
const selectedServiceType = ref<number | null>(null)
const startDate = ref<Date | null>(null)
const endDate = ref<Date | null>(null)

const reportData = ref<ProductionBySupplierReportResponse | null>(null)
const isLoading = ref(false)
const isLoadingSuppliers = ref(false)
const errorMessage = ref<string | null>(null)
const showReportDialog = ref(false)

const fetchedSuppliers = ref<Supplier[]>([])

const serviceTypeOptions = [
  { label: 'All Services', value: null },
  { label: 'Knitting', value: 0 },
  { label: 'Finishing', value: 1 },
  { label: 'Fire Testing', value: 2 },
  { label: 'Quality Check', value: 3 }
]

const supplierOptions = computed(() => {
  const options: { label: string; value: string }[] = []
  if (fetchedSuppliers.value.length > 0) {
    const sortedSuppliers = [...fetchedSuppliers.value].sort((a, b) => {
      const nameA = a.name || ''
      const nameB = b.name || ''
      return nameA.localeCompare(nameB)
    })

    sortedSuppliers.forEach(supplier => {
      if (supplier.id) {
        options.push({
          label: supplier.name || 'Unknown Supplier',
          value: supplier.id
        })
      }
    })
  }
  return options
})

watch(supplierOptions, (newOptions) => {
  if (newOptions.length > 0 && selectedSupplier.value === null) {
    selectedSupplier.value = newOptions[0].value
  } else if (newOptions.length === 0) {
    selectedSupplier.value = null
  }
}, { immediate: true })

onMounted(async () => {
  isLoadingSuppliers.value = true
  try {
    fetchedSuppliers.value = await getSupplierList()
    if (supplierOptions.value.length > 0 && selectedSupplier.value === null) {
      selectedSupplier.value = supplierOptions.value[0].value
    }
  } catch (error: any) {
    console.error('Error fetching supplier options:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load supplier options.',
      life: 3000
    })
  } finally {
    isLoadingSuppliers.value = false
  }
})

const handleGenerateReport = async () => {
  if (!startDate.value || !endDate.value) {
    toast.add({
      severity: 'warn',
      summary: 'Validation Error',
      detail: 'Please select both a start and end date.',
      life: 3000
    })
    return
  }
  if (!selectedSupplier.value) {
    toast.add({
      severity: 'warn',
      summary: 'Validation Error',
      detail: 'Please select a supplier.',
      life: 3000
    })
    return
  }

  isLoading.value = true
  errorMessage.value = null
  reportData.value = null

  try {
    const params: ProductionBySupplierReportParams = {
      startDate: startDate.value.toISOString(),
      endDate: endDate.value.toISOString(),
      supplierId: selectedSupplier.value,
      ...(selectedServiceType.value !== null && { serviceTypes: selectedServiceType.value })
    }

    const data = await getProductionBySupplier(params)
    if (data && data.services && data.services.length > 0) {
      reportData.value = data
      showReportDialog.value = true
    } else {
      reportData.value = null
      toast.add({
        severity: 'info',
        summary: 'No Data',
        detail: 'No production data found for the selected criteria.',
        life: 4000
      })
      showReportDialog.value = false
    }

  } catch (error: any) {
    console.error('Error fetching Production by Supplier report:', error)
    const apiErrorMessage = error.response?.data?.title || error.response?.data?.message || error.message || 'Failed to fetch report data.'
    errorMessage.value = apiErrorMessage
    toast.add({
      severity: 'error',
      summary: 'API Error',
      detail: apiErrorMessage,
      life: 5000
    })
    showReportDialog.value = false
  } finally {
    isLoading.value = false
  }
}

const handleCloseReportDialog = () => {
  showReportDialog.value = false
}
</script>

<template>
  <AuthLayout>
    <div class="min-h-screen p-4 md:p-6">
      <!-- Custom header for this report since it has additional fields -->
      <div class="flex justify-between items-center mb-4">
        <h1 class="text-2xl font-bold text-gray-900">Production by Supplier</h1>
        <Button
            label="Create Report"
            icon="fluent-mdl2:report-add"
            class="p-button-primary"
            @click="handleGenerateReport"
            :disabled="isLoadingSuppliers || supplierOptions.length === 0"
        />
      </div>

      <div class="bg-white p-6 rounded-xl space-y-6">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label for="startDate" class="block text-sm font-medium text-neutral-700 mb-1">Start Date:</label>
            <div class="flex items-center border border-gray-300 rounded-lg overflow-hidden">
              <Calendar
                  inputId="startDate"
                  v-model="startDate"
                  dateFormat="dd/mm/yy"
                  showIcon
                  class="w-full [&_input]:border-none [&_input]:focus:ring-0"
              />
            </div>
          </div>

          <div>
            <label for="endDate" class="block text-sm font-medium text-neutral-700 mb-1">End Date:</label>
            <div class="flex items-center border border-gray-300 rounded-lg overflow-hidden">
              <Calendar
                  inputId="endDate"
                  v-model="endDate"
                  dateFormat="dd/mm/yy"
                  showIcon
                  class="w-full [&_input]:border-none [&_input]:focus:ring-0"
              />
            </div>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label for="supplier" class="block text-sm font-medium text-neutral-700 mb-1">Supplier:</label>
            <Dropdown
                inputId="supplier"
                v-model="selectedSupplier"
                :options="supplierOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Select a Supplier"
                :disabled="isLoadingSuppliers || supplierOptions.length === 0"
                class="w-full"
                :showClear="false"
            />
          </div>

          <div>
            <label for="serviceType" class="block text-sm font-medium text-neutral-700 mb-1">Service Type:</label>
            <Dropdown
                inputId="serviceType"
                v-model="selectedServiceType"
                :options="serviceTypeOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Select Service Type"
                class="w-full"
                :showClear="false"
            />
          </div>
        </div>
      </div>

      <div v-if="isLoading || isLoadingSuppliers" class="flex justify-center items-center mt-10">
        <ProgressSpinner strokeWidth="4" style="width: 50px; height: 50px" animationDuration=".5s" />
        <p class="ml-3 text-lg text-gray-600">
          {{ isLoadingSuppliers ? 'Loading supplier options...' : 'Generating Report...' }}
        </p>
      </div>

      <div v-if="!isLoadingSuppliers && supplierOptions.length === 0 && !isLoading"
           class="mt-6 text-center text-orange-600">
        <p>No suppliers available to generate a report. Please ensure suppliers exist in the system.</p>
      </div>

      <Message v-if="errorMessage && !showReportDialog" severity="error" :closable="true" class="mt-6">
        {{ errorMessage }}
      </Message>

      <div
          v-if="!isLoading && !isLoadingSuppliers && !reportData && !errorMessage && !showReportDialog && supplierOptions.length > 0"
          class="mt-10 text-center text-gray-500">
        <p>Select your criteria and click "Create Report" to view details.</p>
      </div>

      <ProductionBySupplierReportDialog
          :visible="showReportDialog"
          :reportData="reportData"
          :startDate="startDate"
          :endDate="endDate"
          :supplierId="selectedSupplier"
          :serviceType="selectedServiceType"
          :onClose="handleCloseReportDialog"
      />
    </div>
  </AuthLayout>
</template>

<style></style>
